import { trailPoint, trailShot, type SessionAction, type SessionTrail } from './sessionTrail';

/**
 * Clicks numbered on a ticket's screenshot (TOADO-919), shared by the web app
 * (the ticket's picture and its Session window) and the API (the timeline's
 * `mark` and `box`, and the marked screenshot MCP's agents read, TOADO-948),
 * so a person and an agent see the same numbers on the same clicks.
 */

/** One click marked on the ticket's screenshot: its number, its control's box centre in the shot viewport's CSS pixels (scaled to the picture by `markInBox`), and which action it is. */
export interface ClickMark { n: number; x: number; y: number; actionIndex: number }

/** The marks for a ticket's screenshot, and the viewport they are measured in. */
export interface ClickMarks { viewport: { width: number; height: number }; marks: ClickMark[] }

/**
 * The clicks to number on a ticket's screenshot (TOADO-919): those whose
 * control the capture measured in its screenshot, each marked at its box's
 * centre, in the viewport the trail's `shot` names. A capture keeps a box only
 * where the control was wholly in the picture, found in the same place on
 * every frame from just before it to just after, with no scroll, resize,
 * change to the page's DOM or animation seen in between; so no page or scroll
 * is worked out here. Only a change lasting under a frame is not seen.
 * Numbered in order, 1 first.
 */
export function clickMarksForTrail(session: unknown): ClickMarks | null {
  const trail = (session && typeof session === 'object' ? session : {}) as Partial<SessionTrail>;
  const viewport = trailShot(trail.shot);
  if (!viewport) return null;
  const actions = Array.isArray(trail.actions) ? trail.actions : [];
  const marks: ClickMark[] = [];
  actions.forEach((action: SessionAction, actionIndex) => {
    if (!action || action.kind !== 'click') return;
    const box = trailPoint(action.point)?.box;
    if (!box || box.x + box.width > viewport.width || box.y + box.height > viewport.height) return;
    marks.push({ n: marks.length + 1, x: box.x + box.width / 2, y: box.y + box.height / 2, actionIndex });
  });
  return marks.length > 0 ? { viewport, marks } : null;
}

/** The marks for a ticket, read from its capture's trail. */
export function clickMarksFor(ticket: { capture?: { session?: unknown } | null }): ClickMarks | null {
  return clickMarksForTrail(ticket.capture?.session);
}

/**
 * Where a mark sits in a box the screenshot is fitted into with `object-fit:
 * contain` (the annotation canvas's natural box, or the picture's own pixels):
 * scaled to fit, centred.
 */
export function markInBox(mark: Pick<ClickMark, 'x' | 'y'>, viewport: ClickMarks['viewport'], box: { width: number; height: number }): { left: number; top: number } {
  const scale = Math.min(box.width / viewport.width, box.height / viewport.height);
  return {
    left: (box.width - viewport.width * scale) / 2 + mark.x * scale,
    top: (box.height - viewport.height * scale) / 2 + mark.y * scale,
  };
}

/**
 * A mark's control box in a picture of `picture`'s pixels, fitted the same way
 * as `markInBox`: whole pixels, inside the picture.
 */
export function boxInPicture(box: { x: number; y: number; width: number; height: number }, viewport: ClickMarks['viewport'], picture: { width: number; height: number }): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(picture.width / viewport.width, picture.height / viewport.height);
  const left = (picture.width - viewport.width * scale) / 2;
  const top = (picture.height - viewport.height * scale) / 2;
  const x = Math.max(0, Math.round(left + box.x * scale));
  const y = Math.max(0, Math.round(top + box.y * scale));
  return {
    x,
    y,
    width: Math.max(1, Math.min(picture.width - x, Math.round(box.width * scale))),
    height: Math.max(1, Math.min(picture.height - y, Math.round(box.height * scale))),
  };
}
